import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import useRecaptcha from "@/hooks/useRecaptcha";
import Honeypot from "@/components/Honeypot";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------------------
// StickyLeadForm - shared "Request a Demo" form used in the right rail of
// Solution and Presentations feature pages (and as an inline mobile form at
// the bottom of the page).
//
// Props:
//   title    - Feature/solution name, sent as `other_info: "Interested in: ..."`
//   variant  - "desktop" or "mobile" (drives the data-testid prefix only)
//   ctaLabel - Submit button label
//   headline / subheadline - Optional card header overrides
// -----------------------------------------------------------------------------

export default function StickyLeadForm({
  title,
  variant = "desktop",
  ctaLabel = "Schedule Your Personalized Demo",
  headline = "Request a Demo",
  subheadline = "See How Antera Can Transform Your Business",
}) {
  const prefix = variant === "mobile" ? "mobile-lead" : "sticky-lead";
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    phone: "",
    role: "Owner",
    referral_source: "Website",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const recaptcha = useRecaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.email || !formData.company) {
      toast.error("Please fill in required fields.");
      return;
    }
    setLoading(true);
    try {
      const token = await recaptcha.execute("sticky_lead_submit");
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        other_info: `Interested in: ${title}`,
        source_page: typeof window !== "undefined" ? window.location.pathname : "",
        recaptcha_token: token,
        website_url: honeypot,
      });
      setSubmitted(true);
      toast.success("Request submitted!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-slate-900">Thank You!</h3>
        <p className="text-sm text-slate-600 mt-2">We&apos;ll be in touch shortly to schedule your personalized demo.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`${prefix}-form`}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg"
    >
      <Honeypot value={honeypot} onChange={setHoneypot} name={`website_url_${prefix}`} />
      <div className="text-center mb-5">
        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-3">
          <Send className="h-5 w-5 text-sky-600" />
        </div>
        <h3 className="text-lg font-bold text-slate-900">{headline}</h3>
        <p className="text-xs text-slate-500 mt-1">{subheadline}</p>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-600">First Name *</Label>
            <Input
              data-testid={`${prefix}-first-name`}
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="John"
              className="mt-1 h-9 text-sm border-slate-300"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Last Name</Label>
            <Input
              data-testid={`${prefix}-last-name`}
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="Doe"
              className="mt-1 h-9 text-sm border-slate-300"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Work Email *</Label>
          <Input
            data-testid={`${prefix}-email`}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="john@company.com"
            className="mt-1 h-9 text-sm border-slate-300"
            required
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Company *</Label>
          <Input
            data-testid={`${prefix}-company`}
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder="Acme Corp"
            className="mt-1 h-9 text-sm border-slate-300"
            required
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Phone</Label>
          <Input
            data-testid={`${prefix}-phone`}
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="(555) 123-4567"
            className="mt-1 h-9 text-sm border-slate-300"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid={`${prefix}-submit`}
        className="w-full mt-5 bg-sky-600 hover:bg-sky-700 text-white rounded-full h-11 text-sm font-semibold"
      >
        {loading ? "Submitting..." : ctaLabel}
      </Button>
      <p className="text-[10px] text-slate-400 text-center mt-3">
        No commitment required. Every demo is personalized to your business and workflows.
      </p>
    </form>
  );
}
